import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { equals, mergeDeepLeft } from 'ramda';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useAsync, useDebounce, useDeepCompareEffect } from 'react-use';

import { Product } from 'types/Product';
import {
  getProductsUniversal,
  GetProductsUniversalOptions,
  ProductRequestParams,
  ProductsUniversalReturnType,
} from 'actions/productsDB';
import { RootState } from 'reducers';

const defaultOptions: GetProductsUniversalOptions = {
  localFirst: true,
  withMeta: true,
  addToCachedItems: false,
};

const useProducts = (
  props: ProductRequestParams,
  options?: GetProductsUniversalOptions,
  debounceTimeout = 300,
): {
  products: Product[];
  total: number;
  loading: boolean;
  productsDict: Record<number, Product>;
  refresh(): void;
} => {
  const newOptions = mergeDeepLeft(options ?? {}, defaultOptions);
  const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();

  const [memoProps, setMemoProps] = useState(props);
  const [memoOptions, setMemoOptions] = useState(newOptions);
  const [productsPromise, setProductsPromise] = useState<
    Promise<ProductsUniversalReturnType>
  >(
    Promise.resolve({
      products: [],
      total: 0,
      productsDict: {},
    }),
  );
  const {
    value: { products = [], total = 0, productsDict = {} } = {},
    loading,
  } = useAsync(() => productsPromise, [productsPromise]);

  const updateProps = () => {
    if (!equals(memoProps, props)) {
      setMemoProps(props);
    }
  };

  const updateOptions = () => {
    if (!equals(memoOptions, newOptions)) {
      setMemoOptions(newOptions);
    }
  };

  const updateProductPromise = () =>
    setProductsPromise(dispatch(getProductsUniversal(memoProps, memoOptions)));

  useDebounce(
    () => {
      updateProps();
      updateOptions();
    },
    debounceTimeout,
    [props, newOptions],
  );

  useDeepCompareEffect(updateProductPromise, [memoProps, memoOptions]);

  return {
    products,
    productsDict,
    total,
    loading,
    refresh: updateProductPromise,
  };
};

export default useProducts;
